
import "./СontentCourse.css";
import React from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaVk } from "react-icons/fa";
import { SiMax } from "react-icons/si";

import CourseSummary from "../CourseSummary/CourseSummary";
import HeaderLogin from "../HeaderLogin/HeaderLogin";
import Footer from "../../Footer/Footer";

import { CurrentUserContext } from "../../../contexts/CurrentUserContext";
import { api } from "../../../utilit/Api";

function ContentCourse({ isUnit, onReceivingUnitId, onLesson, exitLogin, isLessonAll }) {
  const { currentUser } = React.useContext(CurrentUserContext);
  const navigate = useNavigate();

  // ===== helpers (стабильные) =====
  const sameId = React.useCallback((a, b) => String(a) === String(b), []);
  const includesId = React.useCallback(
    (arr, id) => Array.isArray(arr) && arr.some((x) => sameId(x, id)),
    [sameId]
  );

  // === Платежи пользователя (успешные) ===
  const [myPayments, setMyPayments] = React.useState([]);

  // Прогресс (по факту пройденных уроков)
  const totalLessonAll = isLessonAll.length;
  const completedLessonAll = React.useMemo(
    () => isLessonAll.filter((lesson) => includesId(lesson.completedBy, currentUser._id)).length,
    [isLessonAll, includesId, currentUser._id]
  );
  const progress =
    totalLessonAll > 0 ? Math.round((completedLessonAll / totalLessonAll) * 100) : 0;

  // Загрузка платежей текущего пользователя
  React.useEffect(() => {
    let cancelled = false;
    async function loadPayments() {
      try {
        const res = await api.getMyPayments("succeeded");
        if (!cancelled) setMyPayments(res?.payments || []);
      } catch {
        if (!cancelled) setMyPayments([]);
      }
    }
    if (currentUser?._id) loadPayments();
    return () => {
      cancelled = true;
    };
  }, [currentUser?._id]);

  // Есть ли покупка bundle (все модули)
  const hasBundle = React.useMemo(
    () => (myPayments || []).some((p) => p.type === "bundle" && p.status === "succeeded"),
    [myPayments]
  );

  // Set купленных модулей поштучно (для блока покупки)
  const ownedModuleIds = React.useMemo(() => {
    const set = new Set();
    (myPayments || []).forEach((p) => {
      if (p.type === "module" && p.status === "succeeded" && p.moduleId) {
        set.add(String(p.moduleId));
      }
    });
    return set;
  }, [myPayments]);

  // ==== Модули и цены (для блока покупки) ====
  const paidModules = React.useMemo(
    () => (Array.isArray(isUnit) ? isUnit.filter((m) => Number(m?.price) > 0) : []),
    [isUnit]
  );

  const hasModuleAccessByPayments = React.useCallback(
    (unit) => {
      if (!unit) return false;
      if (Number(unit?.price) <= 0) return true; // бесплатные доступны всем
      if (hasBundle) return true;
      const id = String(unit._id || unit.id || "");
      return ownedModuleIds.has(id);
    },
    [hasBundle, ownedModuleIds]
  );

  const ownedPaidModules = React.useMemo(
    () => paidModules.filter((m) => hasModuleAccessByPayments(m)),
    [paidModules, hasModuleAccessByPayments]
  );

  const nextUnownedModule = React.useMemo(
    () => paidModules.find((m) => !hasModuleAccessByPayments(m)) || null,
    [paidModules, hasModuleAccessByPayments]
  );

  // Полностью ли всё куплено
  const allPaid = paidModules.length > 0 && ownedPaidModules.length === paidModules.length;

  // Скрыть блок покупки (с кнопками), если bundle или всё уже куплено
  const shouldShowBuyCloud = paidModules.length > 0 && !hasBundle && !allPaid;

  // Сумма всех платных модулей и цена со скидкой 10%
  const bundleBasePrice = React.useMemo(
    () => paidModules.reduce((sum, m) => sum + Number(m.price || 0), 0),
    [paidModules]
  );
  const bundleDiscountPrice = Math.round(bundleBasePrice * 0.9);

  // Кнопку «Купить все модули» показываем, только если ещё ничего не куплено (и нет bundle)
  const showBundle = !hasBundle && ownedPaidModules.length === 0;

  // Если на бэке createPayment требует courseId — пытаемся вытащить его из данных модулей
  const courseId =
    isUnit?.[0]?.courseId || isUnit?.[0]?.course?._id || isUnit?.courseId || "courseId";

  const isMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
    navigator.userAgent
  );
  const isAndroid = /Android/i.test(navigator.userAgent);

  const priceFmt = (n) => (Number(n) || 0).toLocaleString("ru-RU");

  const openVK = (e) => {
    if (!isMobile) return;
    e.preventDefault();

    const vkCommunityUrl = "https://vk.com/kt_mebel";
    const intentUrl = `intent://vk.com/kt_mebel#Intent;scheme=vkontakte;package=com.vkontakte.android;S.browser_fallback_url=${encodeURIComponent(vkCommunityUrl)};end`;

    window.location.href = isAndroid ? intentUrl : vkCommunityUrl;
  };

  const openMAX = (e) => {
    if (!isMobile) return;
    e.preventDefault();

    const maxProfileUrl = "https://max.ru/u/f9LHodD0cOKK1HgyrwlY2m3ossoVzFsiEUJmVbzTDzy3d8dqFhzwCLGkLP8";
    const newWin = window.open(maxProfileUrl, "_blank");

    setTimeout(() => {
      try {
        if (!newWin || newWin.closed) {
          window.location.href = maxProfileUrl;
        }
      } catch (error) {
        window.location.href = maxProfileUrl;
      }
    }, 700);
  };

  // ==== Состояния оплат ====
  const [loadingModuleBuy, setLoadingModuleBuy] = React.useState(false);
  const [loadingBundleBuy, setLoadingBundleBuy] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");

  // ==== Хендлеры оплаты ====
  const handleBuyModule = React.useCallback(async () => {
    try {
      setErrorMsg("");
      if (!nextUnownedModule?._id && !nextUnownedModule?.id) {
        setErrorMsg("Нет доступных модулей для покупки");
        return;
      }
      setLoadingModuleBuy(true);

      const moduleId = nextUnownedModule._id || nextUnownedModule.id;

      const payload = {
        userId: currentUser?._id,
        courseId,
        items: [{ type: "module", courseId, moduleId }],
        returnPath: "/payment/return",
      };

      const { id, confirmationUrl } = await api.createPayment(payload);

      if (id) {
        localStorage.setItem("lastPaymentId", id);
      }

      if (confirmationUrl) {
        window.location.href = confirmationUrl;
      } else {
        setErrorMsg("Не удалось получить ссылку для оплаты.");
      }
    } catch (e) {
      setErrorMsg("Ошибка создания платежа. Попробуйте позже.");
    } finally {
      setLoadingModuleBuy(false);
    }
  }, [nextUnownedModule, currentUser?._id, courseId]);

  const handleBuyAll = React.useCallback(async () => {
    try {
      setErrorMsg("");
      if (bundleDiscountPrice <= 0) {
        setErrorMsg("Сумма заказа должна быть больше 0.");
        return;
      }
      setLoadingBundleBuy(true);

      const payload = {
        userId: currentUser?._id,
        courseId,
        items: [{ type: "bundle", courseId }],
        returnPath: "/payment/return",
      };

      const { id, confirmationUrl } = await api.createPayment(payload);

      if (id) {
        localStorage.setItem("lastPaymentId", id);
      }

      if (confirmationUrl) {
        window.location.href = confirmationUrl;
      } else {
        setErrorMsg("Не удалось получить ссылку для оплаты.");
      }
    } catch (e) {
      setErrorMsg("Ошибка создания платежа. Попробуйте позже.");
    } finally {
      setLoadingBundleBuy(false);
    }
  }, [bundleDiscountPrice, currentUser?._id, courseId]);

  // ====== ДОСТУП К УРОКАМ (уровень LESSON) ======
  const hasLessonAccess = React.useCallback(
    (lesson) => {
      if (!lesson) return false;
      if (lesson.isPublic === true) return true;
      const uid = currentUser?._id;
      if (!uid) return false;
      if (includesId(lesson.availableFor, uid)) return true;
      if (includesId(lesson.completedBy, uid)) return true;
      return false;
    },
    [currentUser?._id, includesId]
  );

  // ---------- сортировка уроков по unit.count → lesson.count ----------
  const unitOrderMap = React.useMemo(() => {
    const m = new Map();
    (isUnit || []).forEach((u) => {
      const uid = String(u._id || u.id);
      const uc = Number(u.count);
      m.set(uid, Number.isFinite(uc) ? uc : 999999);
    });
    return m;
  }, [isUnit]);

  const getUnitId = React.useCallback((lesson) => {
    if (!lesson) return null;
    if (typeof lesson.unit === "object") {
      return lesson.unit?._id || lesson.unit?.id || null;
    }
    return lesson.unit || null;
  }, []);

  const lessonsSorted = React.useMemo(() => {
    const arr = Array.isArray(isLessonAll) ? [...isLessonAll] : [];
    arr.sort((a, b) => {
      const ua = getUnitId(a);
      const ub = getUnitId(b);
      const uca = unitOrderMap.get(String(ua)) ?? 999999;
      const ucb = unitOrderMap.get(String(ub)) ?? 999999;

      if (uca !== ucb) return uca - ucb;

      const la = Number(a?.count);
      const lb = Number(b?.count);
      const laOk = Number.isFinite(la) ? la : 999999;
      const lbOk = Number.isFinite(lb) ? lb : 999999;

      if (laOk !== lbOk) return laOk - lbOk;
      return String(a._id).localeCompare(String(b._id));
    });
    return arr;
  }, [isLessonAll, unitOrderMap, getUnitId]);

  // Последний пройденный
  const lastCompleted = React.useMemo(
    () =>
      [...lessonsSorted].reverse().find((l) => includesId(l.completedBy, currentUser._id)) ||
      null,
    [lessonsSorted, includesId, currentUser._id]
  );

  // Навигация в формате /content/:unitId/lessons/:lessonId
  const getUnitIdByLessonId = React.useCallback(
    (lessonId) => {
      const l = isLessonAll.find((x) => sameId(x._id, lessonId));
      if (!l) return null;
      if (typeof l.unit === "object") {
        return l.unit?._id || l.unit?.id || null;
      }
      return l.unit || null;
    },
    [isLessonAll, sameId]
  );

  const goToLesson = React.useCallback(
    (lessonOrId) => {
      const lessonId = typeof lessonOrId === "string" ? lessonOrId : lessonOrId?._id;
      if (!lessonId) return;
      const unitId = getUnitIdByLessonId(lessonId);
      if (!unitId) return;

      if (typeof onLesson === "function") {
        onLesson(lessonId);
      } else {
        navigate(`/content/${unitId}/lessons/${lessonId}`);
      }
    },
    [getUnitIdByLessonId, navigate, onLesson]
  );

  // Поиск первого ДОСТУПНОГО и НЕпройденного урока с индекса
  const findNextAccessibleUncompletedFrom = React.useCallback(
    (startIdx) => {
      if (!Array.isArray(lessonsSorted) || lessonsSorted.length === 0) return null;
      const uid = currentUser?._id;
      for (let i = startIdx; i < lessonsSorted.length; i++) {
        const lesson = lessonsSorted[i];
        const notDone =
          !(Array.isArray(lesson.completedBy) && uid && includesId(lesson.completedBy, uid));
        if (notDone && hasLessonAccess(lesson)) return lesson;
      }
      return null;
    },
    [lessonsSorted, currentUser?._id, includesId, hasLessonAccess]
  );

  // Целевой урок для CTA
  const nextLessonForCta = React.useMemo(() => {
    if (!lessonsSorted.length) return null;

    if (lastCompleted) {
      const idx = lessonsSorted.findIndex((l) => sameId(l._id, lastCompleted._id));
      const next = findNextAccessibleUncompletedFrom(Math.max(0, idx + 1));
      if (next) return next;
      // wrap-around
      return findNextAccessibleUncompletedFrom(0);
    }
    return findNextAccessibleUncompletedFrom(0);
  }, [
    lessonsSorted,
    lastCompleted,
    findNextAccessibleUncompletedFrom,
    sameId,
  ]);

  const nextLessonAvailable = Boolean(nextLessonForCta);

  // ==== Стилевые пресеты кнопок ====
  const baseBtn =
    "group inline-flex items-center justify-center flex-1 sm:flex-none h-12 sm:h-[54px] px-4 sm:px-7 " +
    "rounded-2xl border border-black/5 font-bold tracking-[.2px] " +
    "transition-all duration-150 ease-out focus:outline-none";

  const primaryBtn =
    baseBtn +
    " bg-gradient-to-r from-cyan-400 to-teal-500 text-slate-900 " +
    "shadow-[0_10px_24px_rgba(56,191,242,.28)] " +
    "hover:-translate-y-0.5 hover:shadow-[0_14px_32px_rgba(56,191,242,.32)] " +
    "active:translate-y-0 focus:ring-4 focus:ring-teal-400/30";

  const Icon = () => (
    <svg
      className="ml-2 h-5 w-5 opacity-90 transition-transform duration-150 group-hover:translate-x-0.5"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        d="M13.5 5l7 7-7 7M3.5 12h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const supportLinks = (
    <span className="mt-2 inline-flex flex-wrap items-center gap-x-2 gap-y-1 sm:mt-0">
      <a
        href="https://vk.com/kt_mebel"
        target={isMobile ? "_self" : "_blank"}
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-cyan-700 hover:underline"
        onClick={openVK}
      >
        <FaVk className="h-4 w-4" />
        <span>VK</span>
      </a>
      <span className="text-slate-400">•</span>
      <a
        href="https://max.ru/u/f9LHodD0cOKK1HgyrwlY2m3ossoVzFsiEUJmVbzTDzy3d8dqFhzwCLGkLP8"
        target={isMobile ? "_self" : "_blank"}
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-cyan-700 hover:underline"
        onClick={openMAX}
      >
        <SiMax className="h-4 w-4" />
        <span>MAX</span>
      </a>
      <span className="text-slate-400">•</span>
      <a href="tel:+79011118455" className="text-cyan-700 hover:underline">
        +7 (901) 111-84-55
      </a>
    </span>
  );

  return (
    <>
      <Helmet>
            <title>Школа КТМ | личный кабинет</title>
      </Helmet>
      <HeaderLogin exitLogin={exitLogin} />
      <div className="content-course">
        <div className="content-hero">
          <div className="content-hero__left">
            <p className="content-hero__hello">
              Привет, {currentUser?.name || "ученик"} 👋
            </p>

            <h1 className="content-hero__title">
              Конструктор-технолог мебельного производства
            </h1>

            <div className="content-hero__progress">
              <div className="content-hero__progress-top">
                <span className="content-hero__progress-label">Прогресс курса</span>
              </div>
              <div className="content-hero__progress-bar" aria-label="Прогресс курса">
                <span className="content-hero__progress-fill" style={{ width: `${progress}%` }} />
              </div>
            </div>
          </div>

          <div className="content-hero__right">
            <div className="content-hero__badge" aria-hidden="true">
              <span className="content-hero__badge-num">{progress}%</span>
            </div>
          </div>
        </div>

        {/* CTA блок */}
        <div className="max-w-[1280px] mx-auto mb-6 px-0 flex flex-col sm:flex-row gap-3">
          {nextLessonAvailable ? (
            <button className={primaryBtn} onClick={() => goToLesson(nextLessonForCta)}>
              {lastCompleted ? "Продолжить обучение" : "Начать бесплатно"}
              <Icon />
            </button>
          ) : (
            <button className={baseBtn + " opacity-60 cursor-not-allowed"} disabled>
              Нет доступных уроков
            </button>
          )}
        </div>

        {/* Ошибки оплат */}
        {errorMsg && (
          <div className="max-w-[1280px] mx-auto mb-4 px-2">
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-700 text-sm">
              {errorMsg}
            </div>
          </div>
        )}

        {/* Поздравление при bundle или если всё куплено */}
        {(hasBundle || allPaid) && (
          <div className="max-w-[1280px] mx-auto mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="text-slate-900 font-semibold">🎉 Поздравляем, вы зачислены!</div>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                Старт 10.11.2026
              </span>
            </div>
            <p className="mt-3 text-sm text-slate-700">
              По всем вопросам — поддержка: {supportLinks}
            </p>
          </div>
        )}

        {/* Облачко покупки — если есть платные, не всё куплено и нет bundle */}
        {shouldShowBuyCloud && (
          <div
            className="max-w-[1280px] mx-auto rounded-2xl border border-black/5 bg-gradient-to-b from-sky-50 to-white shadow-[0_10px_30px_rgba(0,0,0,.06)] p-4 sm:p-5 space-y-4"
            role="group"
            aria-labelledby="buy-one-title"
          >
            <div className="flex items-start gap-2">
              <span className="inline-flex shrink-0 h-6 px-2 items-center rounded-full text-xs font-semibold bg-cyan-100 text-cyan-800">
                <span aria-hidden="true" className="mr-0.5">ℹ️</span>
                <span className="hidden sm:inline">Подсказка</span>
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <div id="buy-one-title" className="font-semibold text-slate-900">
                    {ownedPaidModules.length > 0
                      ? "🎉 Поздравляем, вы зачислены на курс! По мере прохождения можете докупать следующие модули."
                      : "Купите первый модуль или весь курс"}
                  </div>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                    Старт 10.11.2026
                  </span>
                </div>

                <p className="mt-2 text-sm text-slate-600">
                  По всем вопросам — поддержка: {supportLinks}
                </p>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-3">
              <button
                className={
                  "group inline-flex items-center justify-center w-full lg:w-auto h-auto sm:h-[54px] px-4 sm:px-7 py-3 sm:py-0 " +
                  "rounded-2xl border border-black/5 font-bold tracking-[.2px] text-center " +
                  "transition-all duration-150 ease-out focus:outline-none " +
                  "bg-white text-slate-900 shadow-sm hover:bg-slate-50 active:bg-slate-100 " +
                  "focus:ring-4 focus:ring-cyan-400/30 " +
                  "flex-col sm:flex-row"
                }
                disabled={!nextUnownedModule || loadingModuleBuy}
                onClick={handleBuyModule}
                title={
                  nextUnownedModule
                    ? `Купить: ${nextUnownedModule.title || "Модуль"}`
                    : "Нет доступных модулей"
                }
              >
                <span className="font-bold whitespace-nowrap">
                  {loadingModuleBuy ? "Создаём платёж..." : "Купить модуль"}
                </span>
                <span className="mt-1 sm:mt-0 sm:ml-2 text-emerald-600 font-extrabold whitespace-nowrap">
                  {priceFmt(nextUnownedModule?.price || 0)} ₽
                </span>
              </button>

              {showBundle && bundleBasePrice > 0 && (
                <button
                  className={
                    "group inline-flex items-center justify-center w-full lg:w-auto h-auto sm:h-[54px] px-4 sm:px-7 py-3 sm:py-0 " +
                    "rounded-2xl border border-black/5 font-bold tracking-[.2px] text-center " +
                    "transition-all duration-150 ease-out focus:outline-none " +
                    "bg-white text-slate-900 shadow-sm hover:bg-slate-50 active:bg-slate-100 " +
                    "focus:ring-4 focus:ring-cyan-400/30 " +
                    "flex-col sm:flex-row"
                  }
                  disabled={loadingBundleBuy}
                  onClick={handleBuyAll}
                >
                  <span className="font-bold whitespace-nowrap">
                    {loadingBundleBuy ? "Создаём платёж..." : "Купить все модули"}
                  </span>
                  <span className="mt-1 sm:mt-0 sm:mx-3 line-through text-slate-400 font-normal text-sm sm:text-base whitespace-nowrap">
                    {priceFmt(bundleBasePrice)} ₽
                  </span>
                  <span className="mt-1 sm:mt-0 inline-flex items-center gap-2">
                    <span className="text-rose-600 font-extrabold whitespace-nowrap">
                      {priceFmt(bundleDiscountPrice)} ₽
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-rose-100 text-rose-600">
                      −10%
                    </span>
                  </span>
                </button>
              )}
            </div>
          </div>
        )}

        <div className="content-block">
          <h2 className="content-block__title">Программа курса</h2>
          <ol className="course-summary__list">
            {isUnit.map((course) => (
              <CourseSummary
                course={course}
                key={course._id}
                onReceivingUnitId={onReceivingUnitId}
                onLesson={onLesson}
                isLessonAll={isLessonAll}
              />
            ))}
          </ol>
        </div>
      </div>

      <Footer />
    </>
  );
}

export default ContentCourse;
